import { resolveStaticArticlePathPrefix, staticArticleAssetPath } from './staticArticleAssets';

export interface StaticArticleMeta {
    contentLocale?: string;
    slug: string;
    title: string;
    excerpt: string;
    author: string | null;
    publishedDate: string | null;
    featuredImage: string | null;
    socialImage?: string | null;
    twitterImage?: string | null;
}

export interface StaticArticleContent extends StaticArticleMeta {
    html: string;
}

export type StaticContentSection = 'news' | 'newsletter';

const cache: Record<string, StaticArticleContent[]> = {};

function nodeRequire(moduleName: string): any {
    if (typeof window !== 'undefined') return null;
    try {
        return eval('require')(moduleName);
    } catch {
        return null;
    }
}

function loadSection(section: StaticContentSection): StaticArticleContent[] {
    if (typeof window !== 'undefined') return [];
    const localePathPrefix = resolveStaticArticlePathPrefix();
    const cacheKey = `${section}:${localePathPrefix}`;
    if (cache[cacheKey]) return cache[cacheKey];

    const fs = nodeRequire('fs');
    const path = nodeRequire('path');
    if (!fs || !path || typeof process === 'undefined' || !process.cwd) {
        cache[cacheKey] = [];
        return cache[cacheKey];
    }

    const sourceDir = path.join(process.cwd(), 'public', 'static', section);
    const source = JSON.parse(fs.readFileSync(path.join(sourceDir, 'index.json'), 'utf8')) as StaticArticleMeta[];
    let translated: StaticArticleMeta[] = [];
    if (localePathPrefix) {
        try {
            translated = JSON.parse(fs.readFileSync(path.join(sourceDir, localePathPrefix, 'index.json'), 'utf8'));
        } catch { /* Missing translated inventory uses English. */ }
    }
    cache[cacheKey] = source.map((original) => {
        const candidate = translated.find(({ slug }) => slug === original.slug);
        if (candidate && candidate.contentLocale !== 'en') {
            try {
                return { ...candidate, contentLocale: candidate.contentLocale ?? localePathPrefix,
                    html: fs.readFileSync(path.join(sourceDir, localePathPrefix, `${original.slug}.html`), 'utf8') };
            } catch { /* Fall back with both the English metadata and body. */ }
        }
        return { ...original, contentLocale: 'en', html: fs.readFileSync(path.join(sourceDir, `${original.slug}.html`), 'utf8') };
    });
    return cache[cacheKey];
}

export function getStaticArticleIndex(section: StaticContentSection): StaticArticleMeta[] {
    return loadSection(section).map(({ html: _html, ...meta }) => meta);
}

export function getStaticArticleContent(
    section: StaticContentSection,
    slug: string | undefined
): StaticArticleContent | null {
    if (!slug) return null;
    return loadSection(section).find((article) => article.slug === slug) || null;
}

export function readEmbeddedJson<T>(id: string): T | null {
    if (typeof document === 'undefined') return null;
    const el = document.getElementById(id);
    if (!el?.textContent) return null;
    try {
        return JSON.parse(el.textContent) as T;
    } catch {
        return null;
    }
}

export function jsonForHtmlScript(value: unknown): string {
    return JSON.stringify(value).replace(/</g, '\\u003c');
}


export async function fetchStaticArticleIndex(section: StaticContentSection, locale?: string): Promise<StaticArticleMeta[]> {
    const sourceResponse = await fetch(staticArticleAssetPath(section, 'index.json', 'en'));
    if (!sourceResponse.ok) throw new Error('English article index unavailable');
    const source: StaticArticleMeta[] = await sourceResponse.json();
    const tag = locale || process.env.EXPO_PUBLIC_PZ_I18N_LOCALE || 'en';
    let translated: StaticArticleMeta[] = [];
    if (resolveStaticArticlePathPrefix(tag)) {
        try {
            const response = await fetch(staticArticleAssetPath(section, 'index.json', tag));
            if (response.ok) translated = await response.json();
        } catch { /* Missing translated inventory uses English. */ }
    }
    return source.map((original) => {
        const candidate = translated.find(({ slug }) => slug === original.slug);
        return candidate ? { ...candidate, contentLocale: candidate.contentLocale ?? tag }
            : { ...original, contentLocale: 'en' };
    });
}

export async function fetchStaticArticle(section: StaticContentSection, slug: string, locale?: string): Promise<StaticArticleContent | null> {
    const index = await fetchStaticArticleIndex(section, locale);
    const meta = index.find((article) => article.slug === slug);
    if (!meta) return null;
    try {
        const response = await fetch(staticArticleAssetPath(section, `${slug}.html`, meta.contentLocale));
        if (response.ok) return { ...meta, html: await response.text() };
    } catch { /* A translated index can outlive its page. */ }
    if (meta.contentLocale !== 'en') return fetchStaticArticle(section, slug, 'en');
    throw new Error('English article content unavailable');
}
